import SOCITypeII from '@cointracker/assets/general/soc-1-type-2.svg?react';
import SOCIITypeII from '@cointracker/assets/general/soc-2-type-2.svg?react';
import { REBRAND_URLS } from '@cointracker/ui';
import { cloneElement } from 'react';
import { Button } from '../Button';
import { NavLink } from '../NavLink';
import { Tag } from '../Tag';
import AppStoreIcon from '../icons/app-store.svg?react';
import CoinTrackerTextLogo from '../icons/cointracker-text-logo.svg?react';
import PlayStoreIcon from '../icons/play-store.svg?react';
import Facebook from '../icons/social/facebook-monochrome.svg?react';
import LinkedIn from '../icons/social/linkedin-monochrome.svg?react';
import Reddit from '../icons/social/reddit-monochrome.svg?react';
import X from '../icons/social/x-monochrome.svg?react';
import YouTube from '../icons/social/youtube-monochrome.svg?react';
import { type ChildAndClassNameProps } from '../types';
import { Body1, Body3, H5 } from '../typography';
import { cn } from '../utils';

export interface FooterProps {
  platformText?: {
    title: string;
    description: string;
  };
  platformButtons?: Array<PlatformButtonProps>;
  primaryNavItems?: Array<FooterNavItemProps>;
  secondaryNavItems?: Array<FooterNavItemProps>;
  tertiaryNavItems?: Array<FooterNavItemProps>;
  lowerNavItems?: Array<FooterNavItemProps>;
}

export const Footer = ({
  platformText = PLATFORM_TEXT,
  platformButtons = PLATFORM_BUTTONS,
  primaryNavItems = PRIMARY_NAV_ITEMS,
  secondaryNavItems = SECONDARY_NAV_ITEMS,
  tertiaryNavItems = TERTIARY_NAV_ITEMS,
  lowerNavItems = LOWER_NAV_ITEMS,
}: FooterProps) => {
  return (
    <footer className="bg-grey-100 z-1 dark relative m-12 flex min-h-[832px] flex-col gap-4 rounded-[24px] md:m-16 xl:m-24">
      <section className="flex w-full flex-grow flex-col justify-between gap-32 border-b border-[#909090] pb-32 pt-32 md:pb-32 md:pt-24 xl:pb-64 xl:pt-64">
        <FooterGrid className="space-between flex flex-grow flex-col flex-wrap gap-32 md:gap-48 lg:flex-row lg:gap-x-48 xl:gap-y-8">
          <div className="flex w-fit flex-shrink flex-grow basis-0 flex-wrap gap-48 md:flex-shrink-0 md:gap-80 lg:flex-nowrap">
            <FooterNavList items={primaryNavItems} sectionTitle="CoinTracker" />
            <FooterNavList items={secondaryNavItems} sectionTitle="Resources" />
            <FooterNavList items={tertiaryNavItems} sectionTitle="Company" />
            <div className="flex gap-24">
              <SOCITypeII />
              <SOCIITypeII />
            </div>
          </div>
          <div className="basis-100 flex max-w-[351px] flex-shrink-0 flex-grow flex-col gap-16">
            <div className="flex flex-col items-start gap-8 text-balance">
              <H5 className="!text-grey-0">{platformText.title}</H5>
              <Body3 className="!text-grey-0 hidden md:block">
                {platformText.description}
              </Body3>
            </div>
            <div className="flex items-start gap-8 text-balance xl:flex-col">
              {platformButtons.map((button) => (
                <PlatformButton key={button.href} {...button} />
              ))}
            </div>
          </div>
        </FooterGrid>
        <FooterGrid>
          <CoinTrackerTextLogo className="text-grey-0 h-full w-full" />
        </FooterGrid>
      </section>
      <section className="w-full border-t border-[#909090]">
        <FooterGrid className="flex flex-col flex-wrap items-start justify-between gap-48 py-16 lg:flex-row lg:items-center lg:gap-24 lg:py-24 xl:gap-0 xl:py-16">
          <ul className="m-0 flex list-none flex-col flex-wrap items-baseline gap-12 p-0 md:flex-row lg:gap-48">
            <li>
              <Body3 className="!text-[#999999]">
                © CoinTracker {currentYear}
              </Body3>
            </li>
            {lowerNavItems.map((item, index) => (
              <FooterNavItem
                key={`${item.href}-${index}`}
                {...item}
                variant="secondary"
              />
            ))}
          </ul>
          <FooterSocialList items={SOCIAL_ITEMS} />
        </FooterGrid>
      </section>
    </footer>
  );
};

const currentYear = new Date().getFullYear();

export interface FooterNavItemProps {
  href: string;
  variant?: 'primary' | 'secondary';
  title: string;
  secondaryItem?: React.ReactNode;
  className?: string;
  TypographyComponent?: React.ComponentType<{
    children: React.ReactNode;
    className?: string;
  }>;
}

const FooterNavItem = ({
  href,
  variant = 'primary',
  title,
  secondaryItem,
  className,
  TypographyComponent = Body1,
}: FooterNavItemProps) => {
  return (
    <li className={cn('flex items-center gap-8', className)}>
      <NavLink
        className="!text-grey-0 hover:!border-grey-0"
        href={href}
        variant={variant}
      >
        <TypographyComponent className="!text-grey-0">
          {title}
        </TypographyComponent>
      </NavLink>
      {secondaryItem}
    </li>
  );
};

interface FooterNavListProps {
  items: Array<FooterNavItemProps>;
  variant?: 'primary' | 'secondary';
  className?: string;
  listClassName?: string;
  sectionTitle?: string;
}

const FooterNavList = ({
  items,
  variant = 'primary',
  className,
  listClassName,
  sectionTitle,
}: FooterNavListProps) => {
  return (
    <div>
      {sectionTitle && (
        <Body3 className="!text-text-secondary mb-12">{sectionTitle}</Body3>
      )}
      <ul
        className={cn(
          'm-0 flex w-fit list-none flex-col gap-16 p-0',
          listClassName,
        )}
      >
        {items.map((item, index) => (
          <FooterNavItem
            key={`${item.href}-${index}`}
            {...item}
            variant={variant}
            className={className}
          />
        ))}
      </ul>
    </div>
  );
};

export interface FooterSocialItemProps {
  href: string;
  icon: React.ReactNode;
  className?: string;
}

const FooterSocialItem = ({ href, icon, className }: FooterSocialItemProps) => {
  return (
    <li className={className}>
      <NavLink href={href} variant="secondary" socialLink>
        {cloneElement(icon as React.ReactElement, {
          className:
            'text-slate-30 block box-content h-16 p-4 w-16 transition-color duration-200 hover:text-grey-0',
        })}
      </NavLink>
    </li>
  );
};

interface FooterSocialListProps {
  items: Array<FooterSocialItemProps>;
  className?: string;
  listClassName?: string;
}

const FooterSocialList = ({
  items,
  className,
  listClassName,
}: FooterSocialListProps) => {
  return (
    <ul
      className={cn(
        'm-0 flex w-full list-none justify-between gap-12 p-0 md:w-auto md:justify-start md:gap-48',
        listClassName,
      )}
    >
      {items.map((item, index) => (
        <FooterSocialItem
          key={`${item.href}-${index}`}
          {...item}
          className={className}
        />
      ))}
    </ul>
  );
};

const FooterGrid = (props: ChildAndClassNameProps) => {
  const { children, className, ...rest } = props;

  return (
    <div className={cn('px-12 md:px-24 lg:px-32', className)} {...rest}>
      {children}
    </div>
  );
};

FooterGrid.displayName = 'FooterGrid';

interface PlatformButtonProps {
  label: string;
  icon: React.ReactNode;
  href: string;
}

const PlatformButton = ({
  label,
  icon,
  href,
  ...props
}: PlatformButtonProps) => (
  <Button
    href={href}
    variant="secondary"
    hideInitialIcon
    className="h-42 hover:!border-text-primary !border-slate-70 !text-grey-0"
    icon={icon}
    {...props}
  >
    {label}
  </Button>
);

const PLATFORM_TEXT = {
  title: 'Download our app',
  description:
    'View your return on investment, your unified transaction history, wallet balances, and much more on the go.',
};
const PLATFORM_BUTTONS = [
  {
    href: REBRAND_URLS.APPLE_APP_STORE,
    icon: <AppStoreIcon />,
    label: 'App store',
  },
  {
    href: REBRAND_URLS.GOOGLE_PLAY_STORE,
    icon: <PlayStoreIcon />,
    label: 'Play store',
  },
];

const PRIMARY_NAV_ITEMS: Array<FooterNavItemProps> = [
  { href: REBRAND_URLS.FEATURES, title: 'How it works' },
  { href: REBRAND_URLS.PRICING, title: 'Pricing' },
  { href: REBRAND_URLS.FULL_SERVICE, title: 'Full service' },
  {
    href: REBRAND_URLS.INTEGRATIONS,
    title: 'Integrations',
  },
  { href: REBRAND_URLS.CALCULATOR, title: 'Profit calculator' },
  { href: REBRAND_URLS.PORTFOLIO_TRACKER, title: 'Portfolio tracker' },
];

const TERTIARY_NAV_ITEMS: Array<FooterNavItemProps> = [
  { href: REBRAND_URLS.ABOUT, title: 'About' },
  { href: REBRAND_URLS.BRAND, title: 'Brand' },
  {
    href: REBRAND_URLS.CAREERS,

    title: 'Careers',
    secondaryItem: (
      <Tag secondary caps>
        Hiring
      </Tag>
    ),
  },
  { href: REBRAND_URLS.TESTIMONIALS, title: 'Testimonials' },
];

const SECONDARY_NAV_ITEMS: Array<FooterNavItemProps> = [
  { href: REBRAND_URLS.PRICES, title: 'Crypto prices' },
  { href: REBRAND_URLS.WALLET, title: 'Crypto wallet lookup' },
  { href: REBRAND_URLS.BLOG, title: 'Blog' },
  { href: REBRAND_URLS.TAX_GUIDE, title: 'Crypto tax guide' },
  { href: REBRAND_URLS.GLOSSARY, title: 'Crypto glossary' },
  { href: REBRAND_URLS.SUPPORT, title: 'Help center' },
];

const LOWER_NAV_ITEMS: Array<FooterNavItemProps> = [
  {
    href: REBRAND_URLS.PRIVACY,
    title: 'Privacy Policy',
    TypographyComponent: Body3,
  },
  {
    href: REBRAND_URLS.TERMS,
    title: 'Terms',
    TypographyComponent: Body3,
  },
  {
    href: REBRAND_URLS.DISCLAIMER,
    title: 'Disclaimer',
    TypographyComponent: Body3,
  },
  {
    href: REBRAND_URLS.SECURITY,
    title: 'Security',
    TypographyComponent: Body3,
  },
  {
    href: REBRAND_URLS.STATUS,
    title: 'Status',
    TypographyComponent: Body3,
  },
];

const SOCIAL_ITEMS: Array<FooterSocialItemProps> = [
  { href: REBRAND_URLS.SOCIAL_MEDIA.X, icon: <X /> },
  { href: REBRAND_URLS.SOCIAL_MEDIA.REDDIT, icon: <Reddit /> },
  { href: REBRAND_URLS.SOCIAL_MEDIA.LINKEDIN, icon: <LinkedIn /> },
  { href: REBRAND_URLS.SOCIAL_MEDIA.FACEBOOK, icon: <Facebook /> },
  { href: REBRAND_URLS.SOCIAL_MEDIA.YOUTUBE, icon: <YouTube /> },
];
